import * as React from 'react';
import { Dispatch, SetStateAction, useContext } from 'react';
import classes from './Menu.module.scss';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum as ButtonColors } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import classNames from 'classnames';
import { SecondaryMenuItemEnum, TabNavigationConfig } from '../../SecondaryNav/SecondaryNav.controller';
import { Icon } from '../../../components/images/Icon/Icon';
import { useNavigate } from 'react-router-dom';
import { capitalizeString } from '../../../helpers/capitalizeString';
import { DevDebugContext } from '../../../providers/DevDebugProvider';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type MobileMenuProps = {
  isMobileView?: boolean;
  closeMenu?: VoidFunction;
};

export type MenuViewProps = MobileMenuProps & {
  visiblePanel: SecondaryMenuItemEnum | null;
  setVisiblePanel: Dispatch<SetStateAction<SecondaryMenuItemEnum | null>>;
};

export type MenuButtonItemType = {
  id: number;
  content: string;
  pathname: string;
  secondaryMenu: SecondaryMenuItemEnum | null;
  icon: IconsEnum;
  action: VoidFunction;
};

export const MenuView = (props: MenuViewProps): JSX.Element => {
  const navigate = useNavigate();
  const { allowedDevDashboardAccess } = useContext(DevDebugContext);
  const { isAccessBlocked } = useSubscriptionStatus();

  const handleCloseMenu = () => {
    if (props.closeMenu) {
      props.closeMenu();
    }
  };
  const menuButtonItems = [
    {
      id: 1,
      content: 'Publish',
      pathname: '/publish',
      secondaryMenu: SecondaryMenuItemEnum.publish,
      icon: IconsEnum.edit,
      action: () => {
        props.setVisiblePanel(SecondaryMenuItemEnum.publish);
      }
    },
    {
      id: 2,
      content: 'Discover',
      pathname: '/discover',
      secondaryMenu: SecondaryMenuItemEnum.discover,
      icon: IconsEnum.search,
      action: () => {
        props.setVisiblePanel(SecondaryMenuItemEnum.discover);
      }
    },
    {
      id: 3,
      content: 'Analyze',
      pathname: '/analyze',
      secondaryMenu: SecondaryMenuItemEnum.analyze,
      icon: IconsEnum.chart,
      action: () => {
        props.setVisiblePanel(SecondaryMenuItemEnum.analyze);
      }
    }
  ];

  if (allowedDevDashboardAccess) {
    menuButtonItems.push({
      id: 4,
      content: 'Dev Dashboard',
      pathname: '/dev',
      secondaryMenu: SecondaryMenuItemEnum.dev,
      icon: IconsEnum.settings,
      action: () => {
        props.setVisiblePanel(SecondaryMenuItemEnum.dev);
      }
    });
  }

  const secondaryMenuContainerClasses = (visible: boolean) =>
    classNames(classes.UlMobileChildren, classes.UlContainer, {
      [classes.UlMobileChildrenInview]: visible
    });

  const getSecondaryMenuList = (secondaryMenuName: SecondaryMenuItemEnum | null): JSX.Element | null => {
    if (!secondaryMenuName || !props.isMobileView) {
      return null;
    }
    const list: JSX.Element[] = TabNavigationConfig[secondaryMenuName].map((item, index) => {
      const secondaryMobileWrapperClasses = classNames({
        'grid-x': true,
        'align-justify': true,
        [classes.SecondaryMenuMobileListItem]: true,
        [classes.SecondaryMenuMobileListItemSelected]: window.location.pathname === item.url
      });
      const labelWrapper = classNames({
        cell: true,
        auto: true,
        [classes.labelWrapper]: true
      });
      const rightArrowWrapperClasses = classNames({
        cell: true,
        shrink: true,
        [classes.secondaryRightArrowWrapper]: true
      });

      return (
        <li
          key={`${secondaryMenuName}-${index}`}
          className={secondaryMobileWrapperClasses}
          onClick={() => {
            props.setVisiblePanel(null);
            navigate({ pathname: item.url });
            handleCloseMenu();
          }}>
          <div className={labelWrapper}>
            <div className={classes.label}>{item.label}</div>
          </div>
          <div className={rightArrowWrapperClasses}>
            <Icon name={IconsEnum.arrowRight} size={'medium'} />
          </div>
        </li>
      );
    });

    const visible = secondaryMenuName === props.visiblePanel;
    return (
      <ul className={secondaryMenuContainerClasses(visible)}>
        <li className={classes.BackArrow}>
          <a onClick={() => props.setVisiblePanel(null)}>
            <Icon name={IconsEnum.arrowLeft} size={'medium'} />
          </a>
        </li>
        <li className={classes.SecondaryMenuTitle}>
          <h2 className={'one-liner'}>{capitalizeString(props.visiblePanel || '')}</h2>
        </li>
        {list}
      </ul>
    );
  };

  const getListItem = (item: MenuButtonItemType): JSX.Element => {
    if (props.isMobileView) {
      const primayMobileWrapperClasses = classNames({
        'grid-x': true,
        'align-justify': true,
        [classes.LiPrimaryMobileWrapper]: true,
        [classes.LiPrimaryMobileWrapperSelected]: window.location.pathname.startsWith(item.pathname)
      });
      const iconWrapperClasses = classNames({
        cell: true,
        shrink: true,
        [classes.iconWrapper]: true
      });
      const labelWrapper = classNames({
        cell: true,
        auto: true,
        [classes.labelWrapper]: true
      });
      const rightArrowWrapperClasses = classNames({
        cell: true,
        shrink: true,
        [classes.rightArrowWrapper]: true
      });

      return (
        <a
          className={primayMobileWrapperClasses}
          onClick={() => {
            item.action();
          }}>
          <div className={iconWrapperClasses}>
            <Icon name={item.icon} size={'medium'} />
          </div>
          <div className={labelWrapper}>
            <div className={classes.label}>{item.content}</div>
          </div>
          <div className={rightArrowWrapperClasses}>
            <Icon name={IconsEnum.arrowRight} size={'medium'} />
          </div>
        </a>
      );
    }
    return (
      <Button
        type={'a'}
        variant={'solid'}
        color={ButtonColors.primary}
        size={'large'}
        action={() => navigate({ pathname: item.pathname })}
        icon={{ side: 'left', value: item.icon }}
        active={window.location.pathname.startsWith(item.pathname)}>
        <span>{item.content}</span>
      </Button>
    );
  };

  const gridClasses = classNames(classes.UlContainer, {
    'grid-x': !props.isMobileView,
    'grid-padding-x': !props.isMobileView
  });

  const liPrimaryClasses = classNames({
    cell: !props.isMobileView,
    shrink: !props.isMobileView,
    [classes.LiPrimaryMobile]: props.isMobileView
  });

  if (isAccessBlocked) {
    return <></>;
  }

  return (
    <ul className={gridClasses}>
      {menuButtonItems.map((item, index) => (
        <li key={`menu-item-${index}`} className={liPrimaryClasses}>
          {getListItem(item)}
          {getSecondaryMenuList(item.secondaryMenu)}
        </li>
      ))}
    </ul>
  );
};
